<template>
  <div :class="$forms.stepperContainer">
    <app-loader :show-loader="getLoadingStatus" />

    <app-delete-dialog :value="deleteDialog"
                       @delete="deleteEmployment(deleteId, deletePerson)"
                       @close="closeDialog">
      Are you sure you want to delete this employment?
    </app-delete-dialog>

    <app-errors-dialog :value="errorsDialog"
                       @close="closeErrorsDialog">
      <div v-if="$v.employment.isTaxpayerCurrentlyUnemployed.$invalid
           && $v.employment.isTaxpayerCurrentlyUnemployed.$dirty">
        <p :class="$forms.errorMessage">
          Please add at least one job or check the checkbox if you are unemployed
        </p>
        <p v-if="isBusinessApp" :class="$forms.errorMessage">
          If you are self-employed, or own your own business, select not currently employed.
          You will enter your business income and expenses in the next section called “Business”.
        </p>
      </div>
    </app-errors-dialog>

    <div :class="$forms.formMainContainer">
      <h2>Employment</h2>
      <div v-if="isBusinessApp">
        <app-info-block>
          If you are self-employed, or own your own business, select not currently
          employed. You will enter your business income and expenses in the next section
          called “Business”.
        </app-info-block>
        <br>
      </div>
      <p v-else>Please add all employers you currently work for at this time.
        Add, Edit or Delete any Employers you may have.  </p>

      <form>
        <div :class="$style.employmentInfo">
          <div :class="$style.employmentTitle">
            <h6>Primary Taxpayer</h6>
            <div :class="$forms.checkboxGroup">
              <app-checkbox-group label="Please check this box if not currently employed."
                                  v-model="$v.employment.isTaxpayerCurrentlyUnemployed.$model"
                                  :value="getTaxpayerNotEmployed"
                                  :tooltip="true"
                                  @input="setTaxpayerNotEmployed">
                <template v-slot:tooltipText>
                  Only check this box of you are currently not working or unemployed
                </template>
              </app-checkbox-group>
            </div>
          </div>

          <app-data-table v-if="showTaxpayerJobs"
                          table-title="List of Employers"
                          add-button="+ Add Job"
                          :headers="employmentHeaders"
                          :items="getTaxpayerJobs"
                          @addNew="displayAddJobSidebar('taxpayer')"
                          empty-text="There is currently no employment added yet.
                        Please click on ‘+Add Job’ to start adding new job.">

            <template v-slot:tableRows>
              <tr v-for="item in getTaxpayerJobs"
                  :key="item.id"
                  :class="$forms.tableDataRow">
                <td v-if="!item.isIndependentContractor">
                  {{ item.currentEmployerName }}
                </td>
                <td v-else>{{ getTaxpayersName }}</td>
                <td>{{ item.grossPay | currency }}</td>
                <td :class="$forms.tableRowButtons">
                  <button :class="$forms.tableBtn"
                          @click.prevent="showDialog(item.id, 'taxpayer')">
                    <app-svg-icon name="ic_delete"
                                  :class="$forms.tableIconDelete"/>
                  </button>
                </td>
                <td :class="$forms.tableRowButtons">
                  <button :class="$forms.tableBtn"
                          @click.prevent="editTaxpayerJob(item.id)">
                    <app-svg-icon name="ic_edit@3x"
                                  :class="$forms.tableIconEdit"/>
                  </button>
                </td>
              </tr>
            </template>

          </app-data-table>

          <div v-else :class="[$forms.formBox, $style.unemployedInfo]">
            <p>Currently Unemployed. If this is a mistake, uncheck the box above.</p>
          </div>

          <div v-if="$v.employment.isTaxpayerCurrentlyUnemployed.$invalid
           && $v.employment.isTaxpayerCurrentlyUnemployed.$dirty">
            <p :class="$forms.errorMessage">
              Please add at least one job or check the checkbox if you are unemployed
            </p>
            <p v-if="isBusinessApp" :class="$forms.errorMessage">
              If you are self-employed, or own your own business, select not currently employed.
              You will enter your business income and expenses in the next section called
              “Business”.
            </p>
          </div>
        </div>

        <div v-if="getMaritalStatus !== 1"  :class="$style.employmentInfo">
          <div :class="$style.employmentTitle">
            <h6>Taxpayer’s Spouse</h6>
            <div :class="$forms.checkboxGroup">
              <app-checkbox-group label="Please check this box if not currently employed."
                                  v-model="$v.employment.isSpouseCurrentlyUnemployed.$model"
                                  :value="getSpouseNotEmployed"
                                  :tooltip="true"
                                  @input="setSpouseNotEmployed">
                <template v-slot:tooltipText>
                  Only check this box of your spouse is currently not working or unemployed
                </template>
              </app-checkbox-group>
            </div>
          </div>

          <app-data-table v-if="showSpouseJobs"
                          table-title="List of Employers"
                          add-button="+ Add Job"
                          :headers="employmentHeaders"
                          :items="getSpouseJobs"
                          @addNew="displayAddJobSidebar('spouse')"
                          empty-text="There is currently no employment added yet.
                        Please click on ‘+Add Job’ to start adding new job.">

            <template v-slot:tableRows>
              <tr v-for="item in getSpouseJobs"
                  :key="item.id"
                  :class="$forms.tableDataRow">
                <td v-if="!item.isIndependentContractor">
                  {{ item.currentEmployerName }}
                </td>
                <td v-else>{{ getSpousesName }}</td>
                <td>{{ item.grossPay | currency }}</td>
                <td :class="$forms.tableRowButtons">
                  <button :class="$forms.tableBtn"
                          @click.prevent="showDialog(item.id, 'spouse')">
                    <app-svg-icon name="ic_delete"
                                  :class="$forms.tableIconDelete"/>
                  </button>
                </td>
                <td :class="$forms.tableRowButtons">
                  <button :class="$forms.tableBtn"
                          @click.prevent="editSpouseJob(item.id,)">
                    <app-svg-icon name="ic_edit@3x"
                                  :class="$forms.tableIconEdit"/>
                  </button>
                </td>
              </tr>
            </template>

          </app-data-table>

          <div v-else :class="[$forms.formBox, $style.unemployedInfo]">
            <p>Currently Unemployed. If this is a mistake, uncheck the box above.</p>
          </div>

          <p v-if="$v.employment.isSpouseCurrentlyUnemployed.$invalid
           && $v.employment.isSpouseCurrentlyUnemployed.$dirty"
             :class="$forms.errorMessage">
            Please add at least one job or check the checkbox if your spouse is unemployed
          </p>
        </div>

      </form>
    </div>

  </div>

</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import formsMixin from '@/app/core/mixins/forms-mixin';
import { USER_ACCESS_TYPES } from '@/constants';

function mustBeTrueIfTaxpayerUnemployed(val) {
  if (this.getTaxpayerJobs.length < 1) {
    return val === true;
  }

  return true;
}

function mustBeTrueIfSpouseUnemployed(val) {
  if (this.getMaritalStatus !== 1 && this.getSpouseJobs.length < 1) {
    return val === true;
  }

  return true;
}

export default {
  data() {
    return {
      step: 2,
      employment: {
        isTaxpayerCurrentlyUnemployed: false,
        isSpouseCurrentlyUnemployed: false,
      },
      employmentHeaders: [
        'Employer Name',
        'Gross Pay',
      ],
      deleteDialog: false,
      deleteId: 0,
      deletePerson: '',
      taxpayerUnemploymentConfirmed: true,
      spouseUnemploymentConfirmed: true,
    };
  },
  methods: {
    ...mapActions([
      'taxpayerJobItemRequest',
      'spouseJobItemRequest',
      'deleteTaxpayerJob',
      'deleteSpouseJob',
      'postTaxpayerEmploymentStatus',
      'postSpouseEmploymentStatus',
    ]),
    /* Reset all fields of the form and reset $v value */
    resetForm() {
      this.setTaxpayerNotEmployed(false);
      this.setSpouseNotEmployed(false);
      this.$v.$reset();
    },
    /* Change the value of the checkbox input */
    setTaxpayerNotEmployed(val) {
      if (this.deleteDialog && !val) {
        this.taxpayerUnemploymentConfirmed = true;
        this.employment.isTaxpayerCurrentlyUnemployed = false;
        return;
      }
      const taxpayerEmployment = !!val;
      if (val && this.getTaxpayerJobs.length > 0) {
        this.taxpayerUnemploymentConfirmed = false;
        this.showDialog(null, 'taxpayer');
      } else {
        this.postTaxpayerEmploymentStatus({
          newItem: taxpayerEmployment,
          userId: this.getUserId,
        });
      }
    },
    /* Change the value of the checkbox input */
    setSpouseNotEmployed(val) {
      if (this.deleteDialog && !val) {
        this.spouseUnemploymentConfirmed = true;
        this.employment.isSpouseCurrentlyUnemployed = false;
        return;
      }
      const spouseEmployment = !!val;
      if (val && this.getSpouseJobs.length > 0) {
        this.spouseUnemploymentConfirmed = false;
        this.showDialog(null, 'spouse');
      } else {
        this.postSpouseEmploymentStatus({
          newItem: spouseEmployment,
          userId: this.getUserId,
        });
      }
    },
    displayAddJobSidebar(person) {
      this.$emit('displayJobSidebar', person);
    },
    showDialog(id, person) {
      this.deleteDialog = true;
      this.deleteId = id;
      this.deletePerson = person;
    },
    closeDialog() {
      if (!this.getTaxpayerUnemploymentConfirmed) {
        this.setTaxpayerNotEmployed(false);
      }
      if (!this.getSpouseUnemploymentConfirmed) {
        this.setSpouseNotEmployed(false);
      }
      this.deleteDialog = false;
      this.deleteId = 0;
      this.deletePerson = '';
    },
    /* Delete job item by click at the Delete button in the dialog */
    deleteEmployment(id, person) {
      if (person === 'taxpayer') {
        if (!id) {
          this.postTaxpayerEmploymentStatus({
            newItem: true,
            userId: this.getUserId,
          }).then(() => {
            this.taxpayerUnemploymentConfirmed = true;
            this.closeDialog();
          });
        } else {
          this.deleteTaxpayerJob({
            id,
            userId: this.getUserId,
          }).then(() => this.closeDialog());
        }
      } else if (person === 'spouse') {
        if (!id) {
          this.postSpouseEmploymentStatus({
            newItem: true,
            userId: this.getUserId,
          }).then(() => {
            this.spouseUnemploymentConfirmed = true;
            this.closeDialog();
          });
        } else {
          this.deleteSpouseJob({
            id,
            userId: this.getUserId,
          }).then(() => this.closeDialog());
        }
      }
    },
    /* Here we don't need to send any data - we
    just emit the confirmation to go to the next step */
    sendFormData() {
      this.$emit('nextStepDone', this.currentStep);
    },
    editTaxpayerJob(id) {
      this.taxpayerJobItemRequest({
        itemId: id,
        userId: this.getUserId,
      }).then(() => {
        this.$emit('editJob', this.getItemDetails, id, 'taxpayer');
      });
    },
    editSpouseJob(id) {
      this.spouseJobItemRequest({
        itemId: id,
        userId: this.getUserId,
      }).then(() => {
        this.$emit('editJob', this.getItemDetails, id, 'spouse');
      });
    },
    /* Set the form fields to the values that were gotten from the server */
    setEmployment() {
      Object.keys(this.getEmploymentStatus).forEach((key) => {
        this.employment[key] = this.getEmploymentStatus[key];
      });
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
      'getItemDetails',
      'getTaxpayerJobs',
      'getSpouseJobs',
      'getEmploymentLoadingStatus',
      'getMaritalStatusData',
      'getEmploymentStatus',
      'getAppAccessType',
    ]),
    /* Get the current value of the checkbox input */
    getTaxpayerNotEmployed() {
      return this.employment.isTaxpayerCurrentlyUnemployed;
    },
    /* Get the current value of the checkbox input */
    getSpouseNotEmployed() {
      return this.employment.isSpouseCurrentlyUnemployed;
    },
    getLoadingStatus() {
      return this.getEmploymentLoadingStatus === 'loading';
    },
    /* Get marital status. If the status is single the spouse information won't be displayed */
    getMaritalStatus() {
      return this.getMaritalStatusData.maritalStatus;
    },
    getTaxpayerUnemploymentConfirmed() {
      return this.taxpayerUnemploymentConfirmed;
    },
    getSpouseUnemploymentConfirmed() {
      return this.spouseUnemploymentConfirmed;
    },
    showTaxpayerJobs() {
      return !this.employment.isTaxpayerCurrentlyUnemployed
        || !this.getTaxpayerUnemploymentConfirmed;
    },
    showSpouseJobs() {
      return !this.employment.isSpouseCurrentlyUnemployed
        || !this.getSpouseUnemploymentConfirmed;
    },
    getTaxpayersName() {
      return localStorage.getItem('user-name');
    },
    getSpousesName() {
      return `${this.getMaritalStatusData.spouse.firstName} ${this.getMaritalStatusData.spouse.lastName}`;
    },
    isBusinessApp() {
      return this.getAppAccessType === USER_ACCESS_TYPES.Business;
    },
  },
  watch: {
    /* Watch when the server data are downloaded to set form fields */
    getEmploymentStatus() {
      this.setEmployment();
    },
  },
  mixins: [
    formsMixin,
  ],
  validations: {
    employment: {
      isTaxpayerCurrentlyUnemployed: {
        mustBeTrueIfTaxpayerUnemployed,
      },
      isSpouseCurrentlyUnemployed: {
        mustBeTrueIfSpouseUnemployed,
      },
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
@import "@/styles/variables.scss";

.employmentInfo {
  margin-bottom: 30px;
}

.unemployedInfo {
  padding: 18px 18px 2px;
  text-align: center;
}

</style>
